import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { AuthedImage } from '@/components/shared/AuthedImage';
import {
  AlertTriangle, User, FileText, Phone, Heart, Users, ListChecks,
  Activity, ClipboardCheck, ChevronRight, CircleAlert,
  Target, Clock, Calendar, HeartPulse, ShieldAlert, Wallet, Pencil,
} from 'lucide-react';
import { getBrandName } from '@/lib/brandName';

function money(v) {
  if (v === null || v === undefined || v === '') return null;
  try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(v); }
  catch { return String(v); }
}

const SEV = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-300',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-300',
  INFORMATION: 'bg-blue-100 text-blue-800 border-blue-300',
};

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(d).slice(0, 10); }
}

const NDIS_BADGE = {
  ACTIVE: 'bg-green-100 text-green-800', EXPIRING_SOON: 'bg-amber-100 text-amber-800',
  EXPIRED: 'bg-red-100 text-red-800', MISSING: 'bg-slate-100 text-slate-600',
};

function SectionCard({ title, icon: Icon, onView, children, testId, headerRight }) {
  return (
    <Card data-testid={testId} className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-primary" />} {title}
        </CardTitle>
        <div className="flex items-center gap-2">
          {headerRight}
          {onView && (
            <button onClick={onView} className="text-xs text-primary hover:underline flex items-center" data-testid={`${testId}-view`}>
              View details <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="text-sm flex-1">{children}</CardContent>
    </Card>
  );
}

const Row = ({ label, value, className = '', 'data-testid': testId }) => (
  <div className={`flex justify-between gap-3 py-0.5 ${className}`} data-testid={testId}>
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right truncate">{value || <span className="text-slate-400">—</span>}</span>
  </div>
);

export function ParticipantOverviewDashboard({ participantId, goToTab, resolveTab }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let active = true;
    setData(null); setError(null); setDenied(false);
    api.get(`/participants/${participantId}/overview`)
      .then(r => active && setData(r.data.data))
      .catch(e => {
        if (!active) return;
        const status = e?.response?.status;
        const code = e?.response?.data?.error?.code;
        if (status === 403 || code === 'PARTICIPANT_NOT_ASSIGNED') {
          setDenied(true);
        } else {
          setError(e?.userMessage || 'Failed to load overview');
        }
      });
    return () => { active = false; };
  }, [participantId]);

  const nav = (tab) => goToTab ? goToTab(tab) : navigate(`/participants/${participantId}?tab=${tab}`);
  // A target is navigable only when it resolves to a visible/permitted in-page tab.
  const canNav = (tab) => (resolveTab ? !!resolveTab(tab) : true);
  const onViewFor = (tab) => (canNav(tab) ? () => nav(tab) : undefined);

  if (denied) return (
    <div className="p-6" data-testid="overview-access-denied">
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Access restricted</p>
          <p className="text-xs text-amber-700">You are not assigned to this participant, so their overview is not available to you.</p>
        </div>
      </div>
    </div>
  );
  if (error) return <div className="p-6 text-red-600" data-testid="overview-error">{error}</div>;
  if (!data) return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="overview-loading">
      {[...Array(6)].map((_, i) => <div key={i} className="h-40 bg-slate-100 rounded-lg animate-pulse" />)}
    </div>
  );

  const s = data.participantSnapshot, n = data.ndisSummary, pc = data.profileCompletion;
  const criticalAlerts = (data.keyAlerts || []).filter(a => a.severity === 'CRITICAL');

  return (
    <div className="space-y-4" data-testid="participant-overview-dashboard">
      {/* Snapshot */}
      <Card data-testid="overview-snapshot">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-4 items-start">
          <AuthedImage src={s.profile_photo_url} alt={s.full_name}
            className="w-20 h-20 rounded-full object-cover border-2 border-primary/20 shrink-0"
            fallback={<div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><User className="w-8 h-8 text-primary" /></div>} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold truncate">{s.full_name}</h2>
              {s.preferred_name && s.preferred_name !== s.full_name && <span className="text-sm text-muted-foreground">"{s.preferred_name}"</span>}
              <Badge variant="outline">{s.status || 'N/A'}</Badge>
              {s.is_archived && <Badge className="bg-amber-100 text-amber-800">Archived</Badge>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mt-2 text-xs">
              <Row label="ID" value={s.participant_code} />
              <Row label="NDIS" value={s.ndis_number} />
              <Row label="DOB" value={`${fmtDate(s.date_of_birth)}${s.age != null ? ` (${s.age})` : ''}`} />
              <Row label="Pronouns" value={s.pronouns} />
              <Row label="Phone" value={s.phone} />
              <Row label="Email" value={s.email} />
              <Row label="Commenced" value={fmtDate(s.service_commencement)} />
              <Row label="Plan ends" value={fmtDate(s.ndis_plan_end)} />
              <Row
                label="Address"
                value={s.address}
                className="col-span-2 sm:col-span-3"
                data-testid="overview-snapshot-address"
              />
              <Row
                label="Service Coordinator"
                value={(data.assignedTeam || []).find((m) => m && m.role === 'Service Coordinator')?.name}
                data-testid="overview-snapshot-coordinator"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Critical alerts banner */}
      {criticalAlerts.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3" data-testid="overview-critical-alerts">
          <div className="flex items-center gap-2 text-red-800 font-semibold text-sm mb-1"><CircleAlert className="w-4 h-4" /> Critical Alerts</div>
          <div className="flex flex-wrap gap-2">
            {criticalAlerts.map((a, i) => (
              <Badge key={i} className={SEV.CRITICAL} variant="outline">{a.type}: {a.description}</Badge>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Profile completion */}
        <SectionCard title="Profile Completion" icon={ClipboardCheck} testId="overview-completion">
          <div className="flex items-center gap-3 mb-2">
            <Progress value={pc.overall_percentage} className="flex-1" />
            <span className="font-bold text-lg">{pc.overall_percentage}%</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2">{pc.missing_required_count} required item(s) missing{pc.critical_count ? `, ${pc.critical_count} critical` : ''}</p>
          <div className="space-y-1">
            {pc.categories.filter(c => c.status !== 'complete').slice(0, 5).map((c, i) => {
              const clickable = canNav(c.link);
              const Inner = (
                <>
                  <span className="flex items-center gap-1">
                    {c.severity === 'critical' ? <AlertTriangle className="w-3 h-3 text-red-500" /> : <CircleAlert className="w-3 h-3 text-amber-500" />}
                    {c.category}
                  </span>
                  {clickable && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                </>
              );
              return clickable ? (
                <button key={i} onClick={() => nav(c.link)} className="w-full flex items-center justify-between text-xs hover:bg-slate-50 rounded px-1 py-0.5" data-testid={`overview-completion-item-${i}`}>
                  {Inner}
                </button>
              ) : (
                <div key={i} className="w-full flex items-center justify-between text-xs px-1 py-0.5" data-testid={`overview-completion-item-${i}`}>
                  {Inner}
                </div>
              );
            })}
            {pc.categories.every(c => c.status === 'complete') && <p className="text-xs text-green-600">All tracked categories complete ✓</p>}
          </div>
        </SectionCard>

        {/* Key alerts */}
        <SectionCard title={`Key Alerts (${data.keyAlerts.length})`} icon={AlertTriangle} testId="overview-alerts" onView={onViewFor('risks')}>
          {data.keyAlerts.length === 0 ? <p className="text-xs text-muted-foreground">No active alerts recorded.</p> : (
            <div className="space-y-1.5">
              {data.keyAlerts.slice(0, 6).map((a, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Badge variant="outline" className={`${SEV[a.severity] || SEV.HIGH} text-[10px]`}>{a.severity}</Badge>
                  <div className="min-w-0"><span className="font-medium">{a.type}</span>{a.description && <span className="text-muted-foreground"> — {a.description}</span>}</div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* NDIS */}
        <SectionCard title="NDIS Information" icon={FileText} testId="overview-ndis" onView={onViewFor('ndis')}
          headerRight={<Badge className={`text-[10px] ${NDIS_BADGE[n.plan_status] || ''}`}>{n.plan_status}{n.days_remaining != null && n.days_remaining >= 0 ? ` · ${n.days_remaining}d` : ''}</Badge>}>
          <Row label="NDIS Number" value={n.ndis_number} />
          <Row label="Plan start" value={fmtDate(n.plan_start)} />
          <Row label="Plan end" value={fmtDate(n.plan_end)} />
          <Row label="Plan mgmt" value={n.plan_management_type} />
          <Row label="Plan manager" value={n.plan_manager} />
          <Row label="Coordinator" value={n.support_coordinator} />
          {n.local_area_coordinator && <Row label="LAC" value={n.local_area_coordinator} />}
          <Row label="Review date" value={fmtDate(n.plan_review_date)} />
        </SectionCard>

        {/* Personal details */}
        <SectionCard title="Personal Details" icon={User} testId="overview-personal" onView={onViewFor('overview')}>
          <Row label="Legal name" value={data.personalDetails.legal_name} />
          <Row label="Preferred" value={data.personalDetails.preferred_name} />
          <Row label="Language" value={data.personalDetails.preferred_language} />
          <Row label="Interpreter" value={data.personalDetails.interpreter_required ? 'Yes' : null} />
          <Row label="Address" value={data.personalDetails.address} />
        </SectionCard>

        {/* Emergency contacts */}
        <SectionCard title={`Emergency Contacts (${data.emergencyContactsTotal})`} icon={Phone} testId="overview-contacts" onView={onViewFor('contacts')}>
          {data.emergencyContacts.length === 0 ? <p className="text-xs text-amber-600">No emergency contact recorded.</p> : (
            <div className="space-y-2">
              {data.emergencyContacts.map((c, i) => (
                <div key={i} className="border-b last:border-0 pb-1.5">
                  <div className="flex items-center gap-2"><span className="font-medium">{c.name}</span>
                    {c.is_guardian && <Badge variant="outline" className="text-[10px]">Guardian</Badge>}</div>
                  <div className="text-xs text-muted-foreground">{c.relationship} · {c.phone || 'no phone'}</div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Preferences */}
        <SectionCard title="Choices & Preferences" icon={Heart} testId="overview-preferences" onView={onViewFor('preferences')}>
          {Object.keys(data.preferencesSummary).length === 0 ? <p className="text-xs text-amber-600">No preferences recorded yet.</p> : (
            <div className="space-y-1">
              {Object.entries(data.preferencesSummary).slice(0, 5).map(([k, v]) => (
                <Row key={k} label={k.replace(/_/g, ' ')} value={Array.isArray(v) ? v.join(', ') : String(v).slice(0, 60)} />
              ))}
            </div>
          )}
        </SectionCard>

        {/* Assigned team */}
        <SectionCard title="Assigned Team" icon={Users} testId="overview-team" onView={onViewFor('support-team')}
          headerRight={onViewFor('support-team') && (   /* #3: edit entry point — team is managed in Care & Support → Support Team */
            <button onClick={onViewFor('support-team')} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="overview-team-edit">
              <Pencil className="w-3 h-3" /> Edit team
            </button>
          )}>
          {data.assignedTeamWarnings.map((w, i) => (
            <div key={i} className="text-xs text-amber-600 flex items-center gap-1 mb-1"><AlertTriangle className="w-3 h-3" />{w}</div>
          ))}
          {data.assignedTeamWarnings.length > 0 && (
            <p className="text-[11px] text-muted-foreground mb-1">Add a Primary Support Worker / Key Worker and a Support Coordinator / Case Manager in Support Team to clear these.</p>
          )}
          {data.assignedTeam.map((t, i) => (
            <div key={i} className="flex items-center justify-between py-0.5">
              <span className="font-medium truncate">{t.name}</span>
              <span className="text-xs text-muted-foreground">{t.role}</span>
            </div>
          ))}
        </SectionCard>

        {/* Outstanding tasks */}
        <SectionCard title={`Outstanding Tasks (${data.outstandingTasks.length})`} icon={ListChecks} testId="overview-tasks" onView={onViewFor('tasks')}>
          {data.outstandingTasks.length === 0 ? <p className="text-xs text-muted-foreground">No outstanding tasks 🎉</p> : (
            <div className="space-y-1.5">
              {data.outstandingTasks.slice(0, 6).map((t, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="truncate">{t.title}</span>
                  <span className="text-xs text-muted-foreground">{fmtDate(t.due_date)}</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Recent activity */}
        <SectionCard title="Recent Activity" icon={Activity} testId="overview-activity" onView={onViewFor('timeline')}>
          {data.recentActivity.length === 0 ? <p className="text-xs text-muted-foreground">No recent activity.</p> : (
            <div className="space-y-1.5">
              {data.recentActivity.slice(0, 6).map((a, i) => (
                <div key={i} className="text-xs">
                  <span className="font-medium">{a.description}</span>
                  <span className="text-muted-foreground"> · {fmtDate(a.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Goals & Outcomes */}
        <SectionCard title={`Goals & Outcomes (${(data.goalsOutcomes?.active_count) ?? 0})`} icon={Target} testId="overview-goals" onView={onViewFor('goals')}>
          {(!data.goalsOutcomes || data.goalsOutcomes.active_goals.length === 0) ? (
            <p className="text-xs text-muted-foreground">No active goals recorded.</p>
          ) : (
            <div className="space-y-1.5">
              {data.goalsOutcomes.active_goals.slice(0, 5).map((g, i) => (
                <div key={i} className="text-xs" data-testid={`overview-goal-${i}`}>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium truncate">{g.title}</span>
                    <span className="text-muted-foreground">{g.progress_percent != null ? `${g.progress_percent}%` : '—'}</span>
                  </div>
                  {typeof g.progress_percent === 'number' && <Progress value={g.progress_percent} className="h-1 mt-0.5" />}
                  {g.at_risk && <span className="text-[10px] text-amber-600">At risk</span>}
                </div>
              ))}
              <div className="text-[11px] text-muted-foreground pt-1">
                Achieved: {data.goalsOutcomes.achieved_count} · At risk: {data.goalsOutcomes.at_risk_count}
              </div>
            </div>
          )}
        </SectionCard>

        {/* My Routine (derived) */}
        <SectionCard title="My Routine" icon={Clock} testId="overview-routine" onView={onViewFor('preferences')}>
          {(!data.routineSummary || data.routineSummary.items.length === 0) ? (
            <p className="text-xs text-muted-foreground">{data.routineSummary?.message || 'No structured routine is currently available.'}</p>
          ) : (
            <div className="space-y-1.5">
              {data.routineSummary.items.map((r, i) => (
                <div key={i} className="text-xs" data-testid={`overview-routine-${i}`}>
                  <span className="font-medium">{r.label}: </span>
                  <span className="text-muted-foreground">{r.detail}</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Upcoming events */}
        <SectionCard title={`Upcoming Events (${(data.upcomingEvents || []).length})`} icon={Calendar} testId="overview-events" onView={onViewFor('schedule')}>
          {(data.upcomingEvents || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No upcoming events.</p>
          ) : (
            <div className="space-y-1.5">
              {data.upcomingEvents.slice(0, 6).map((e, i) => (
                <div key={i} className="flex justify-between gap-2 text-xs" data-testid={`overview-event-${i}`}>
                  <span className="truncate"><Badge variant="outline" className="mr-1 text-[10px]">{e.type}</Badge>{e.title || ''}</span>
                  <span className="text-muted-foreground whitespace-nowrap">{fmtDate(e.date)}{e.time && !String(e.time).includes('-') ? ` ${String(e.time).slice(0,5)}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Clinical overview (permission-aware) */}
        {data.clinicalOverview && (
          <SectionCard title="Clinical Overview" icon={HeartPulse} testId="overview-clinical" onView={onViewFor('clinical')}>
            <div className="space-y-1 text-xs">
              <Row label="Active conditions" value={data.clinicalOverview.conditions.length || '0'} />
              <Row label="Allergies" value={data.clinicalOverview.allergies.length || '0'} />
              <Row label="Active medications" value={data.clinicalOverview.medication_count || '0'} />
              {data.clinicalOverview.allergies.slice(0, 3).map((a, i) => (
                <div key={i} className="text-[11px] text-red-700" data-testid={`overview-clinical-allergy-${i}`}>⚠ {a.allergen}{a.severity ? ` (${a.severity})` : ''}</div>
              ))}
              {(data.clinicalOverview.conditions.length + data.clinicalOverview.allergies.length + data.clinicalOverview.medication_count) === 0 &&
                <p className="text-muted-foreground">No clinical records recorded.</p>}
            </div>
          </SectionCard>
        )}

        {/* Risk & safeguarding overview (permission-aware) */}
        {data.riskOverview && (
          <SectionCard title="Risk & Safeguarding" icon={ShieldAlert} testId="overview-risk" onView={onViewFor('risks')}>
            <div className="space-y-1 text-xs">
              <div className="flex gap-3">
                <span className="text-red-700 font-medium">Critical: {data.riskOverview.critical_count}</span>
                <span className="text-orange-700 font-medium">High: {data.riskOverview.high_count}</span>
                {data.riskOverview.overdue_reviews > 0 && <span className="text-amber-700">Overdue reviews: {data.riskOverview.overdue_reviews}</span>}
              </div>
              {data.riskOverview.recent_incidents.slice(0, 3).map((inc, i) => (
                <div key={i} className="text-[11px] text-muted-foreground" data-testid={`overview-risk-incident-${i}`}>
                  Incident: {inc.type || 'Incident'} · {fmtDate(inc.date)}{inc.ndis_reportable ? ' · NDIS' : ''}
                </div>
              ))}
              {data.riskOverview.restrictive_practice_count > 0 &&
                <div className="text-[11px] text-purple-700">Restrictive practices: {data.riskOverview.restrictive_practice_count}</div>}
              {(data.riskOverview.active_risks.length + data.riskOverview.recent_incidents.length) === 0 &&
                <p className="text-muted-foreground">No active risks or recent incidents.</p>}
            </div>
          </SectionCard>
        )}

        {/* Funding overview (permission-aware) */}
        {data.fundingOverview && (
          <SectionCard title="Funding Overview" icon={Wallet} testId="overview-funding" onView={onViewFor('funding')}>
            {(!data.fundingOverview.active_plan || !data.fundingOverview.active_plan.total_budget) ? (
              <p className="text-xs text-muted-foreground">No funding plan available.</p>
            ) : (() => {
              const fp = data.fundingOverview.active_plan;
              return (
                <div className="space-y-1 text-xs" data-testid="overview-funding-plan">
                  <Row label="Status" value={fp.status} />
                  <Row label="Total budget" value={money(fp.total_budget) || 'Not available'} />
                  <Row label="Used" value={money(fp.used_budget) || 'Not available'} />
                  <Row label={`Tracked remaining (${getBrandName()})`} value={money(fp.remaining_budget) || 'Not available'} />
                  <div className="text-[10px] text-muted-foreground" data-testid="tracked-remaining-note">Tracked internally by {getBrandName()}; may not equal the live NDIS plan balance (e.g. multiple providers).</div>
                  {typeof fp.utilisation_percent === 'number' && <Progress value={fp.utilisation_percent} className="h-1 mt-1" />}
                  {typeof fp.utilisation_percent === 'number' && <div className="text-[11px] text-muted-foreground">{fp.utilisation_percent}% utilised</div>}
                  <Row label="Plan end" value={fmtDate(fp.plan_end)} />
                </div>
              );
            })()}
          </SectionCard>
        )}
      </div>
    </div>
  );
}

export default ParticipantOverviewDashboard;
